import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useServerFn } from '@tanstack/react-start'
import { useAuth } from '@/hooks/use-auth'
import { getEmailHealthOverview } from '@/lib/admin-email-health.functions'
import { AlertTriangle, CheckCircle2, Clock, Inbox } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/admin/email-health')({
  head: () => ({
    meta: [{ title: 'Admin — E-mail queue health' }, { name: 'robots', content: 'noindex' }],
  }),
  component: EmailHealthPage,
})

function fmt(ts: string | null) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })
}

function EmailHealthPage() {
  const { isAdmin, loading } = useAuth()
  const fn = useServerFn(getEmailHealthOverview)
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin-email-health'],
    queryFn: () => fn({}),
    enabled: isAdmin,
    refetchInterval: 30_000,
  })

  if (loading) return <div className="p-8">Laden…</div>
  if (!isAdmin) return <div className="p-8">Geen toegang.</div>

  const snap = data?.snapshot
  const alerts = data?.alerts ?? []
  const healthy = snap && snap.stuckCount === 0 && snap.dlq24h === 0

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">E-mail queue health</h1>
          <p className="text-muted-foreground">
            Laatste health-check alerts en huidige queue status. Vernieuwt elke 30s.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="text-sm rounded-md border px-3 py-1.5 hover:bg-muted"
          disabled={isFetching}
        >
          {isFetching ? 'Vernieuwen…' : 'Vernieuw'}
        </button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-28 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              icon={healthy ? CheckCircle2 : AlertTriangle}
              label="Status"
              value={healthy ? 'Gezond' : 'Aandacht nodig'}
              accent={healthy ? 'text-emerald-600' : 'text-amber-600'}
            />
            <StatCard
              icon={Clock}
              label={`Stuck > 10 min`}
              value={snap?.stuckCount ?? 0}
              accent={snap?.stuckCount ? 'text-amber-600' : ''}
              sub={snap?.oldestPending ? `Oudste pending: ${fmt(snap.oldestPending)}` : 'Geen pending'}
            />
            <StatCard
              icon={Inbox}
              label="DLQ (24u)"
              value={snap?.dlq24h ?? 0}
              accent={snap?.dlq24h ? 'text-destructive' : ''}
            />
          </div>

          <Section title="Recente alerts" empty="Nog geen alerts geregistreerd.">
            {alerts.length > 0 && (
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground uppercase">
                  <tr>
                    <th className="text-left pb-2">Tijd</th>
                    <th className="text-left pb-2">Severity</th>
                    <th className="text-left pb-2">Stuck</th>
                    <th className="text-left pb-2">DLQ</th>
                    <th className="text-left pb-2">Bericht</th>
                    <th className="text-left pb-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a: any) => {
                    const ctx = (a.context ?? {}) as Record<string, any>
                    return (
                      <tr key={a.id} className="border-t align-top">
                        <td className="py-2 whitespace-nowrap">{fmt(a.created_at)}</td>
                        <td className="py-2">
                          <SeverityBadge severity={a.severity} />
                        </td>
                        <td className="py-2 font-mono">{ctx.stuckCount ?? '—'}</td>
                        <td className="py-2 font-mono">{ctx.dlqCount ?? ctx.dlq24h ?? '—'}</td>
                        <td className="py-2 text-muted-foreground">{a.message}</td>
                        <td className="py-2">
                          {a.resolved_at ? (
                            <span className="text-xs text-emerald-600">Opgelost</span>
                          ) : (
                            <span className="text-xs text-amber-600">Open</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </Section>

          <Section title="Laatste DLQ-berichten (24u)" empty="Geen DLQ-berichten in de afgelopen 24 uur.">
            {(snap?.recentDlq.length ?? 0) > 0 && (
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground uppercase">
                  <tr>
                    <th className="text-left pb-2">Tijd</th>
                    <th className="text-left pb-2">Template</th>
                    <th className="text-left pb-2">Ontvanger</th>
                    <th className="text-left pb-2">Fout</th>
                  </tr>
                </thead>
                <tbody>
                  {snap?.recentDlq.map((r: any) => (
                    <tr key={r.id} className="border-t align-top">
                      <td className="py-2 whitespace-nowrap">{fmt(r.created_at)}</td>
                      <td className="py-2 font-mono">{r.template_name}</td>
                      <td className="py-2">{r.recipient_email}</td>
                      <td className="py-2 text-destructive text-xs">{r.error_message ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </>
      )}
    </div>
  )
}

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
  sub,
}: {
  icon: any
  label: string
  value: number | string
  accent?: string
  sub?: string
}) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-start justify-between mb-2">
        <Icon className={`size-5 ${accent ?? 'text-muted-foreground'}`} />
      </div>
      <div className={`text-2xl font-bold ${accent ?? ''}`}>{value}</div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </div>
  )
}

function Section({
  title,
  empty,
  children,
}: {
  title: string
  empty: string
  children: React.ReactNode
}) {
  const hasContent = Array.isArray(children) ? children.some(Boolean) : Boolean(children)
  return (
    <div className="rounded-xl border bg-card p-5">
      <h2 className="font-semibold mb-4">{title}</h2>
      {hasContent ? children : <p className="text-sm text-muted-foreground">{empty}</p>}
    </div>
  )
}

function SeverityBadge({ severity }: { severity: string }) {
  const map: Record<string, string> = {
    info: 'bg-blue-100 text-blue-800',
    warning: 'bg-amber-100 text-amber-800',
    error: 'bg-red-100 text-red-800',
    critical: 'bg-red-200 text-red-900',
  }
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${map[severity] ?? 'bg-muted'}`}>
      {severity}
    </span>
  )
}
